import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { GUARDIAN_HELD_BELOW_AGE } from "../pages/Register";

/**
 * Extracted from `AdminUsersPanel` (issue #626 — that component was 47 lines
 * over the `max-lines-per-function` budget). Pure markup for the #569
 * set-date-of-birth dialog: `dobTarget` / `dobValue` / `dobError` /
 * `dobSaving` / `dobGuardianHeld` and the actual write (`saveDob`, which
 * resolves the player-profile id and PUTs `/players/:id`) all still live in
 * `AdminUsersPanel`. This component only renders the dialog and reports the
 * value change, cancel and save upward.
 *
 * The guardian threshold is imported, not re-declared, for the same reason
 * `AdminUsersPanel` imports it: one number, shared with the sign-up form
 * (`pages/Register.jsx`), never two copies that could drift apart.
 */
export default function AdminSetDobDialog({
  dobTarget,
  dobValue,
  dobError,
  dobSaving,
  dobGuardianHeld,
  onOpenChange,
  onValueChange,
  onCancel,
  onSave,
}) {
  return (
    <Dialog open={!!dobTarget} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="admin-dob-dialog">
        <DialogHeader>
          <DialogTitle>Set date of birth{dobTarget ? ` — ${dobTarget.display_name}` : ""}</DialogTitle>
          <DialogDescription>
            Saved to the player's profile — the same field sign-up collects. "Age unknown" means
            exactly that: until a date of birth is on file, nothing may treat this player as an
            adult.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0">
          <Label htmlFor="admin-dob-input">Date of birth</Label>
          <Input
            id="admin-dob-input"
            type="date"
            value={dobValue}
            onChange={(e) => onValueChange(e.target.value)}
            className="mt-1"
            data-testid="admin-dob-input"
          />
          {dobError && (
            <p className="text-xs text-red-600 mt-1" data-testid="admin-dob-error">{dobError}</p>
          )}
        </div>
        {/* The sign-up form's guardian note (Register.jsx, #169), shown at
            the same threshold with its wording reused verbatim — minus the
            register-only instruction about which email/password to enter,
            which has no referent here (this dialog carries no credential
            fields). */}
        {dobGuardianHeld && (
          <div
            className="rounded-lg border-2 p-4 min-w-0"
            style={{ borderColor: "#0D1B3E" }}
            data-testid="admin-dob-guardian"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#5D9D35" }}>
              Under {GUARDIAN_HELD_BELOW_AGE} — a guardian holds the sign-in
            </p>
            <p className="mt-2 text-sm text-slate-700">
              The account is in the player's name and stays theirs. While they are under{" "}
              {GUARDIAN_HELD_BELOW_AGE}, a parent or guardian holds the sign-in details.
            </p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={dobSaving}
            data-testid="admin-dob-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={dobSaving || !dobValue}
            style={{ background: "#0B2E59" }}
            data-testid="admin-dob-save"
          >
            {dobSaving ? "Saving…" : "Save date of birth"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
